import * as amplitude from '@amplitude/analytics-browser';
import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { finalize, map, mergeMap, Observable, ReplaySubject, take, tap } from 'rxjs';
import {
  ExpressPayCharge,
  ExpressPayComponent,
} from 'shared/src/components/express-pay/express-pay.component';
import { ApiService, BillingPeriodType, Placement, Product } from 'shared/src/services/api.service';
import { AxonService } from 'shared/src/services/axon.service';
import { LocalStorageKey, LocalStorageService } from 'shared/src/services/local-storage.service';
import { NewsbreakService } from 'shared/src/services/newsbreak.service';
import { PaymentDataService } from 'shared/src/services/payment-data.service';
import { PaymentHandlerService } from 'shared/src/services/payment-handler.service';
import { UserInfoService } from 'shared/src/services/user-info.service';
import { formatCurrency } from 'shared/src/utils/currency.util';
import { PaymentData } from '../payment/payment.component';

export interface ProductView extends Product {
  title: string;
  compareAtPriceLabel: string;
  fullPriceLabel: string;
  priceLabel: string;
  dailyPriceLabel: string;
  isBestDeal: boolean;
  isMostPopular: boolean;
  discountLabel?: string;
  trialLabel?: string;
  periodLabel: string;
  durationInDays: number;
}

@Component({
  selector: 'app-checkout',
  imports: [CommonModule, MatButtonModule, MatProgressSpinnerModule, ExpressPayComponent],
  templateUrl: './checkout.component.html',
  styleUrl: './checkout.component.scss',
})
export class CheckoutComponent implements OnInit, OnChanges {
  @Input() placement: Placement;
  @Input() email: string | undefined; //TODO: maybe track if it changes to rebuild needed stuff
  /**
   * In-app path the result page should offer as a way back, for hosts that have one (the web
   * app's discount paywall). Funnels leave it unset and the result page is unchanged.
   */
  @Input() successRedirect: string | undefined;
  @Input() stripeCustomerId: string | undefined;
  @Input() name: string | undefined;
  @Input() insufficientFundsAction: (() => void) | undefined;

  @Input() isDisclaimerShown: boolean = true;

  @Input() isPeopleBadgeShown: boolean = false;

  @Input() isSafeCheckoutNoteShown: boolean = true;

  @Input() moneyBackGuaranteeMode: 'small' | 'large' = 'small';

  @Input() selectedProduct: ProductView;
  @Output() selectedProductChange = new EventEmitter<Product>();

  @Output() discountTitle = new EventEmitter<string>();
  @Output() purchaseButtonTitle = new EventEmitter<string>();
  @Output() canceledPurchase = new EventEmitter<void>();

  currentPurchaseButtonTitle: string = $localize`:@@continue:Continue`;
  readonly processingLabel = $localize`:@@processingYourPayment:Processing your payment...`;

  products: ProductView[] = [];

  isLoading: boolean = true;

  @ViewChild(ExpressPayComponent) private expressPay?: ExpressPayComponent;

  // True when the current `email` came from a wallet sheet rather than the host — only such
  // emails may be replaced by a later collection (retry through a different wallet account).
  private isEmailCollected = false;

  private productsReady$ = new ReplaySubject<void>(1);
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private api: ApiService,
    private router: Router,
    private paymentHandler: PaymentHandlerService,
    private paymentDataService: PaymentDataService,
    private axonService: AxonService,
    private newsbreakService: NewsbreakService,
    private localStorageService: LocalStorageService,
    private userInfo: UserInfoService
  ) {}

  ngOnInit(): void {
    this.purchaseButtonTitle.emit(this.currentPurchaseButtonTitle);
  }

  // The amount the wallet sheet authorizes — the first charge, not the recurring price.
  get initialCharge(): number {
    return this.selectedProduct ? this.getInitialCharge(this.selectedProduct) : 0;
  }

  // Arrow property: <app-express-pay> calls this back, so `this` must stay bound.
  createSubscriptionCharge = (): Observable<ExpressPayCharge> =>
    this.paymentHandler
      .createSubscription({
        productId: this.selectedProduct.id,
        email: this.email,
        customerId: this.stripeCustomerId,
        name: this.name,
        amplitudeDeviceId: amplitude.getDeviceId(),
      })
      .pipe(tap(({ stripeCustomerId }) => this.userInfo.setStripeCustomerId(stripeCustomerId)));

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['placement'] &&
      changes['placement'].currentValue !== changes['placement'].previousValue
    ) {
      this.loadProducts();
    }

    if (
      changes['selectedProduct'] &&
      changes['selectedProduct'].currentValue !== changes['selectedProduct'].previousValue
    ) {
      this.setPurchaseButtonTitle();
    }
  }

  loadProducts() {
    this.isLoading = true;

    this.api
      .getProducts(this.placement)
      .pipe(
        take(1),
        map(products => products.map(product => this.mapProductView(product))),
        tap(products => (this.products = products)),
        map(() => this.getDefaultSelectedProduct()),
        tap(product => this.setSelectedProduct(product)),
        tap(() => this.markMostPopularProduct()),
        tap(() => this.productsReady$.next()),
        finalize(() => {
          this.isLoading = false;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  // The subscription could not be created, so no wallet charge was attempted and no modal has
  // been shown yet.
  handleChargeFailure(error: unknown): void {
    this.paymentHandler.handlePaymentError(
      error,
      undefined,
      this.email,
      this.selectedProduct?.code,
      'subscription',
      this.successRedirect
    );
  }

  handlePaymentSuccess(): void {
    this.localStorageService.set(LocalStorageKey.paymentValue, {
      value: this.getInitialCharge(this.selectedProduct),
      currency: this.selectedProduct.currency,
    });

    this.paymentHandler.navigateToSuccess(
      this.selectedProduct.code,
      this.userInfo.getStripeCustomerId() || '',
      this.successRedirect
    );
  }

  /**
   * The wallet sheet collected the payer's email for a visitor we could not identify upfront.
   * Emitted before createSubscriptionCharge runs, so setting it here is what puts the email on
   * the subscription (and lets the backend map this device's analytics to the user). A
   * host-resolved email is never overwritten — it decides which account the subscription lands
   * on — but a previously *collected* one is: a retry may go through a different wallet
   * account, and the purchase must be filed under the address of the attempt that succeeds.
   */
  adoptWalletEmail(email: string): void {
    if (this.email && !this.isEmailCollected) return;

    this.isEmailCollected = true;
    this.email = email;
    this.userInfo.setEmail(email);
  }

  // The wallet button was tapped; <app-express-pay> is already opening the sheet.
  expressPayClicked(): void {
    amplitude.track('web_express_payment_clicked');

    this.axonService.beginCheckout({
      item: this.selectedProduct.code,
      value: this.getInitialCharge(this.selectedProduct),
      currency: this.selectedProduct.currency,
    });
    //this.newsbreakService.beginCheckout();
  }

  /**
   * Also called from outside by the paywall's sticky CTA, which mirrors whatever the in-page
   * button does: open the wallet sheet when one is available, otherwise go to full checkout.
   */
  purchaseButtonClicked() {
    // Before products load there is no express component yet, and otherPaymentMethodsClicked
    // waits on productsReady$ — so an early click still lands on checkout.
    if (this.expressPay) {
      this.expressPay.activate();
      return;
    }

    this.otherPaymentMethodsClicked();
  }

  otherPaymentMethodsClicked() {
    this.productsReady$
      .pipe(
        take(1),
        mergeMap(() => this.navigateToPayment()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  navigateToPayment() {
    const paymentData: PaymentData = {
      product: this.selectedProduct,
      email: this.email,
      stripeCustomerId: this.stripeCustomerId,
      name: this.name,
      buttonTitle: this.currentPurchaseButtonTitle,
      insufficientFundsAction: this.insufficientFundsAction,
      isDisclaimerShown: this.isDisclaimerShown,
      successRedirect: this.successRedirect,
    };

    this.paymentDataService.setPaymentData(paymentData);

    return this.router.navigate(['/checkout']);
  }

  productClicked(product: ProductView) {
    this.setSelectedProduct(product);
  }

  private setSelectedProduct(product: ProductView) {
    this.selectedProductChange.emit(product);
    this.selectedProduct = product;
    this.setPurchaseButtonTitle();
    this.discountTitle.emit(product.discountLabel);

    // Track view_item for Axon pixel when product is selected
    this.axonService.viewItem({
      item: product.code,
      value: this.getInitialCharge(product),
      currency: product.currency,
    });
  }

  private setPurchaseButtonTitle() {
    //TODO: add the logic here to check if it is free trial, of there is a discount etc
    const trialDays = this.selectedProduct.trialDays;

    if (trialDays > 0) {
      this.currentPurchaseButtonTitle = this.buildTryTrialButtonTitle(trialDays);
    } else {
      this.currentPurchaseButtonTitle = $localize`:@@continue:Continue`;
    }

    this.purchaseButtonTitle.emit(this.currentPurchaseButtonTitle);
  }

  private getDefaultSelectedProduct() {
    let bestProduct;

    if (this.products.length === 1) {
      bestProduct = this.products[0];
    } else {
      const defaultProduct = this.products.find(product => product.isDefault);

      if (defaultProduct) {
        bestProduct = defaultProduct;
      } else {
        bestProduct = this.products.sort((p1, p2) => p1.durationInDays - p2.durationInDays)[0];
      }
    }

    if (bestProduct) {
      bestProduct.isMostPopular = true;
    }

    return bestProduct;
  }

  private markMostPopularProduct() {
    if (!this.products.length) {
      return;
    }

    const bestProductCode = [...this.products].sort(
      (p1, p2) => p2.durationInDays - p1.durationInDays
    )[0].code;
    const bestProduct = this.products.find(product => product.code === bestProductCode);

    if (bestProduct) {
      bestProduct.isBestDeal = true;
    }
  }

  private mapProductView(product: Product): ProductView {
    const trialDays = product.trialDays;
    const trialLabel = trialDays > 0 ? this.buildTrialDaysLabel(trialDays) : undefined;
    const periodLabel = this.getPeriodLabel(product.billingPeriodAmount, product.billingPeriodType);
    const durationInDays = this.getDurationInDays(
      product.billingPeriodAmount,
      product.billingPeriodType
    );
    const discountLabel = this.getDiscountLabel(product);
    const initialCharge = this.getInitialCharge(product);
    const priceLabel = formatCurrency(product.currency, initialCharge);
    const fullPriceLabel = formatCurrency(product.currency, product.recurringPrice);
    const compareAtPriceLabel = formatCurrency(product.currency, product.compareAtPrice);
    const title = this.getProductTitle(periodLabel);

    let dailyPriceLabel = '';
    if (product.introductoryDailyPrice) {
      dailyPriceLabel = formatCurrency(product.currency, product.introductoryDailyPrice);
    } else if (product.dailyPrice) {
      dailyPriceLabel = formatCurrency(product.currency, product.dailyPrice);
    }

    return {
      ...product,
      title,
      isBestDeal: false,
      isMostPopular: false,
      priceLabel,
      fullPriceLabel,
      compareAtPriceLabel,
      dailyPriceLabel,
      discountLabel,
      trialLabel,
      periodLabel,
      durationInDays,
    };
  }

  private getInitialCharge(product: Product): number {
    return (
      product.firstPayment ||
      product.setupFeePrice ||
      product.introductoryRecurringPrice ||
      product.recurringPrice
    );
  }

  private getProductTitle(periodLabel: string): string {
    return this.buildPlanTitle(periodLabel);
  }

  private buildTryTrialButtonTitle(trialDays: number): string {
    // Localized as a single message so each locale can control word order (e.g. French).
    return $localize`:@@tryTrialButton:Try ${trialDays}:trialDays:-Day Trial`;
  }

  private buildTrialDaysLabel(trialDays: number): string {
    // Localized as a single message so each locale can control grammar and spacing.
    return $localize`:@@trialDaysLabel:${trialDays}:trialDays:-Day Trial`;
  }

  private buildPlanTitle(periodLabel: string): string {
    // Localized as a single message so each locale can control word order.
    return $localize`:@@planTitle:${periodLabel}:periodLabel: Plan`;
  }

  private getPeriodLabel(amount: number, periodType: BillingPeriodType): string {
    const unit = this.getPeriodUnit(periodType);
    const label = amount === 1 ? unit.singular : unit.plural;

    return `${amount}-${label}`;
  }

  private getPeriodUnit(periodType: BillingPeriodType): { singular: string; plural: string } {
    switch ((periodType || '').toUpperCase()) {
      case 'DAY':
        return {
          singular: $localize`:@@daySingular:Day`,
          plural: $localize`:@@dayPlural:Days`,
        };
      case 'WEEK':
        return {
          singular: $localize`:@@weekSingular:Week`,
          plural: $localize`:@@weekPlural:Weeks`,
        };
      case 'MONTH':
        return {
          singular: $localize`:@@monthSingular:Month`,
          plural: $localize`:@@monthPlural:Months`,
        };
      case 'YEAR':
        return {
          singular: $localize`:@@yearSingular:Year`,
          plural: $localize`:@@yearPlural:Years`,
        };
      default: {
        const normalized = (periodType || '').toLowerCase();
        return { singular: normalized, plural: `${normalized}s` };
      }
    }
  }

  private getDurationInDays(amount: number, periodType: BillingPeriodType): number {
    switch ((periodType || '').toUpperCase()) {
      case 'DAY':
        return amount;
      case 'WEEK':
        return amount * 7;
      case 'MONTH':
        return amount * 30;
      case 'YEAR':
        return amount * 365;
      default:
        return amount;
    }
  }

  private getDiscountLabel(product: Product): string | undefined {
    if (!product.discount) {
      return;
    }

    const units = product.discount.units;

    if (units === 'PERCENTAGE') {
      return `${product.discount.amount}%`;
    }

    return formatCurrency(product.currency, product.discount.amount);
  }
}
